// Staking maths in ONE place so the Staking tab and the dashboard charts can
// never disagree.
//
// Two different questions, two different answers:
//   * estDailyYieldCa  — FORWARD projection: what today's locked CA should pay
//     per day. Only CA that is still locked earns, so a released/closed order
//     (current_principal 0) contributes nothing; the original contract amount
//     (ca_principal) is a fallback for positions we never synced.
//   * earnedByDateCa   — what was ACTUALLY earned. Prefer the imported
//     `/staking-entries` rows (CaryPact's real per-day reward records). When a
//     wallet has never been history-imported we only know each order's real
//     lifetime `earned_rewards`, so it is spread across the days the order was
//     live using its own accrual curve — the daily shape is derived, but the
//     totals are the real money.

export const earningCa = (p) =>
  (typeof p.current_principal === "number" ? p.current_principal : (p.ca_principal || 0));

/** CA earned per day by every position that had started by `onDate` (all of them when omitted). */
export const estDailyYieldCa = (positions, onDate) =>
  (positions || []).reduce(
    (a, p) => a + (!onDate || (p.date_staked && p.date_staked <= onDate) ? earningCa(p) * (p.daily_rate || 0) : 0),
    0,
  );

const addDays = (iso, n) => {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

const daysBetween = (a, b) => Math.round((new Date(b + "T00:00:00Z") - new Date(a + "T00:00:00Z")) / 86400000);

/**
 * date → CA actually earned, reconstructed from each order's REAL lifetime
 * rewards. Every order's daily amounts sum to its `earned_rewards`.
 */
export const earnedByDateCa = (positions, today) => {
  const out = {};
  (positions || []).forEach((p) => {
    const total = p.earned_rewards || 0;
    if (!total || !p.date_staked || p.date_staked > today) return;
    let n = daysBetween(p.date_staked, today);
    if (p.staking_type === "fixed" && p.term_days) n = Math.min(n, p.term_days);
    n = Math.max(1, Math.min(n, 400));
    const r = p.daily_rate || 0;
    const compound = p.auto_compound !== false && r > 0;
    const weights = [];
    let sum = 0;
    for (let i = 1; i <= n; i += 1) {
      const w = compound ? Math.pow(1 + r, i - 1) : 1;
      weights.push(w);
      sum += w;
    }
    weights.forEach((w, i) => {
      const d = addDays(p.date_staked, i + 1);
      if (d > today) return;
      out[d] = (out[d] || 0) + (total * w) / sum;
    });
  });
  return out;
};

const groupBy = (rows, key) => {
  const m = {};
  (rows || []).forEach((r) => { const k = r[key] || ""; (m[k] = m[k] || []).push(r); });
  return m;
};

/**
 * date → USD staking income for the charts. CaryPact's imported per-day rows are
 * used wherever they exist; every wallet they don't fully cover keeps the REST of
 * its real lifetime rewards, spread over the days the import doesn't speak for.
 *
 * Merging per WALLET matters: CaryPact only hands over reward records for the
 * wallet whose connection you used, and only for the last few days. An earlier
 * version switched the whole chart to imported-only as soon as one row existed,
 * so importing 7 days for 8 wallets flattened 120 days of real staking income to
 * zero on every chart while the tile above still read the true total.
 * Returns { byDate, source: "imported" | "reconstructed" | "mixed" | "none" }.
 */
export const stakingIncomeByDate = (entries, positions, caPrice, today) => {
  const rows = (entries || []).filter((e) => (e.ca_earned || 0) > 0);
  const byDate = {};
  const add = (d, usd) => { if (d && usd) byDate[d] = (byDate[d] || 0) + usd; };
  const entriesByWallet = groupBy(rows, "wallet_id");
  const posByWallet = groupBy(positions, "wallet_id");
  let imported = false, reconstructed = false;

  rows.forEach((e) => {
    add(e.date, e.usd_value || (e.ca_earned || 0) * caPrice);
    imported = true;
  });

  Object.entries(posByWallet).forEach(([wid, pos]) => {
    const own = entriesByWallet[wid] || [];
    const covered = new Set(own.map((e) => e.date));
    const remaining = pos.reduce((a, p) => a + (p.earned_rewards || 0), 0)
      - own.reduce((a, e) => a + (e.ca_earned || 0), 0);
    if (remaining <= 1e-9) return;
    const free = Object.entries(earnedByDateCa(pos, today)).filter(([d]) => !covered.has(d));
    const shape = free.reduce((a, [, v]) => a + v, 0);
    if (shape <= 0) return;
    free.forEach(([d, v]) => add(d, (v / shape) * remaining * caPrice));
    reconstructed = true;
  });

  const source = imported && reconstructed ? "mixed"
    : imported ? "imported"
      : (reconstructed ? "reconstructed" : "none");
  return { byDate, source };
};

/* CaryPact's BOT pledge is the same product in another asset, and the data arrives
   the same way: each order knows its lifetime `earned_rewards`, while the dated
   daily records only exist from the day a wallet was first swept. So it follows the
   CA rule exactly — real dated rows where we have them, each order's remaining
   lifetime rewards spread over the days it was live everywhere else. Returns USD
   per day, because a BOT amount and a CA amount can't be added. */
export const botStakingIncomeByDate = (entries, positions, botPrice, today) => {
  const rows = (entries || []).filter((e) => (e.bot_earned || 0) > 0);
  const byDate = {};
  const add = (d, usd) => { if (d && usd > 0) byDate[d] = (byDate[d] || 0) + usd; };
  const entriesByWallet = groupBy(rows, "wallet_id");
  const posByWallet = groupBy(positions || [], "wallet_id");

  rows.forEach((e) => add(e.date, e.usd_value || (e.bot_earned || 0) * (e.bot_price || botPrice)));

  Object.entries(posByWallet).forEach(([wid, pos]) => {
    const own = entriesByWallet[wid] || [];
    const covered = new Set(own.map((e) => e.date));
    const remaining = pos.reduce((a, p) => a + (p.earned_rewards || 0), 0)
      - own.reduce((a, e) => a + (e.bot_earned || 0), 0);
    if (remaining <= 1e-9) return;
    // The pledge pays a flat daily rate, so the days an order was live share its
    // rewards evenly — no compounding curve to guess at.
    const free = [];
    pos.forEach((p) => {
      if (!p.date_staked) return;
      const live = Math.max(1, daysBetween(p.date_staked, today));
      for (let i = 1; i <= live; i++) {
        const d = addDays(p.date_staked, i);
        if (d > today || covered.has(d)) continue;
        free.push([d, p.earned_rewards || 0]);
      }
    });
    const shape = free.reduce((a, [, v]) => a + v, 0);
    if (shape <= 0) return;
    free.forEach(([d, v]) => add(d, (v / shape) * remaining * botPrice));
  });

  const total = Object.values(byDate).reduce((a, v) => a + v, 0);
  return { byDate, total, dated: rows.length };
};

/** BOT principal live on a given day, stepping in on each order's stake date. */
export const botStakedOnDate = (positions, date) =>
  (positions || []).reduce((a, p) => (
    p.date_staked && p.date_staked <= date
      ? a + (typeof p.current_principal === "number" ? p.current_principal : (p.bot_principal || 0))
      : a), 0);

/** CA capital put to work by the stake contracts live on `date` — the real money in,
 *  so it steps up the day you stake and never moves in between. CA moved in by an
 *  internal "switch to long-term" is netted off the destination order, because the
 *  source order still carries it; otherwise a switch reads as fresh capital. */
export const capitalStakedOnDate = (positions, date) =>
  (positions || []).reduce((a, p) => (
    p.date_staked && p.date_staked <= date
      ? a + Math.max(0, (p.ca_principal || 0) - (p.switch_in_ca || 0))
      : a), 0);

/** The same money, split by how much of its history we can actually trust.
 *
 *  A FIXED term is a contract: one amount, locked on one day, and CaryPact's
 *  `originalPrincipal` never moves again — so it can be dated exactly.
 *
 *  A FLEXIBLE stake is an ACCOUNT, not a contract. CaryPact keeps one order per
 *  wallet and its principal grows with every top-up AND with every auto-compounded
 *  reward, while `createTime` stays the day the account opened. So its amount is a
 *  TODAY figure wearing a past date — back-dating the lump is what put a vertical
 *  cliff in the capital line (Tara: +532.66 CA on 2026-07-16) and left the chart
 *  claiming 708.9 CA was staked on a day CaryPact's own balance read 375.87. */
export const capitalSplitOnDate = (positions, date) =>
  (positions || []).reduce((a, p) => {
    if (!p.date_staked || p.date_staked > date) return a;
    const ca = Math.max(0, (p.ca_principal || 0) - (p.switch_in_ca || 0));
    if (p.term_days) a.fixed += ca;
    else a.flexible += ca;
    return a;
  }, { fixed: 0, flexible: 0 });

/** date → CA rewards actually earned that day. Same rule as `stakingIncomeByDate`
 *  (CaryPact's dated records first, each order's remaining lifetime rewards spread
 *  over the days it was live for everything they don't cover) but kept in CA,
 *  because the wealth curve switches between CA and USD and an imported row's
 *  historical dollar value can't be converted back. */
export const stakingRewardCaByDate = (entries, positions, today) => {
  const rows = (entries || []).filter((e) => (e.ca_earned || 0) > 0);
  const byDate = {};
  const add = (d, ca) => { if (d && ca) byDate[d] = (byDate[d] || 0) + ca; };
  const entriesByWallet = groupBy(rows, "wallet_id");
  const posByWallet = groupBy(positions, "wallet_id");
  rows.forEach((e) => add(e.date, e.ca_earned || 0));
  Object.entries(posByWallet).forEach(([wid, pos]) => {
    const own = entriesByWallet[wid] || [];
    const covered = new Set(own.map((e) => e.date));
    const remaining = pos.reduce((a, p) => a + (p.earned_rewards || 0), 0)
      - own.reduce((a, e) => a + (e.ca_earned || 0), 0);
    if (remaining <= 1e-9) return;
    const free = Object.entries(earnedByDateCa(pos, today)).filter(([d]) => !covered.has(d));
    const shape = free.reduce((a, [, v]) => a + v, 0);
    if (shape <= 0) return;
    free.forEach(([d, v]) => add(d, (v / shape) * remaining));
  });
  return byDate;
};

/**
 * The staking wealth curve, one row per day, every figure a REAL CA number:
 *   capitalCa — the stake contracts live that day
 *   rewardCa  — rewards earned up to that day
 *   totalCa   — CaryPact's own My Staking balance, carried forward between syncs;
 *               before its first reading, capital + rewards
 *
 * Nothing is reconciled. Earlier versions derived capital as (balance − rewards) and
 * scaled it by a factor to make the three lines agree, which left the capital line
 * below the money actually staked and could slide downhill a little every day.
 * Capital + rewards sitting ABOVE the balance is not an error — it is principal a
 * finished term has released, and rewards already claimed out of staking.
 *
 * The one thing we WILL NOT draw: capital above the balance CaryPact measured that
 * day. Fixed terms can't cause that (a contract's amount is fixed on its date), but
 * a flexible account's principal is a today-figure on an opening date, so it is
 * capped at whatever room the day's reading leaves after the fixed contracts —
 * never scaled, never derived, just not allowed to exceed what was in the account.
 */
export const buildStakingCurve = ({ positions, entries, snapshots, start, end }) => {
  if (!start || !end || start > end) return [];
  const reward = stakingRewardCaByDate(entries, positions, end);
  const snapAt = {};
  (snapshots || []).forEach((s) => {
    if (s && s.date && typeof s.staked_ca === "number") snapAt[s.date] = s.staked_ca;
  });
  // Days before the first sync get that first reading as their ceiling: the account
  // can't have held more than the earliest amount we ever measured in it without us
  // inventing the deposits, and inventing them is what drew the cliff.
  const readDates = Object.keys(snapAt).sort();
  const firstRead = readDates.length ? snapAt[readDates[0]] : null;
  // Rewards earned before the window opens still happened — carry them in, so the
  // line lands on the real lifetime total and not just the part on screen.
  let run = Object.entries(reward).reduce((a, [d, v]) => (d < start ? a + v : a), 0);
  let held = null;
  const out = [];
  let d = start;
  while (d <= end && out.length < 800) {
    run += reward[d] || 0;
    if (snapAt[d] != null) held = snapAt[d];
    const { fixed, flexible } = capitalSplitOnDate(positions, d);
    const ceiling = held != null ? held : firstRead;
    const flexCa = ceiling == null ? flexible : Math.min(flexible, Math.max(0, ceiling - fixed));
    const capitalCa = fixed + flexCa;
    out.push({
      date: d,
      capitalCa,
      rewardCa: run,
      totalCa: held != null ? held : capitalCa + run,
      read: held != null,
      capped: flexCa < flexible - 1e-9,
    });
    d = addDays(d, 1);
  }
  return out;
};
